/**
 * RequirementsStateManager
 * 
 * Purpose: Manage complex form state transitions separate from API interaction.
 * This manager handles all UI state logic independently of React component lifecycle.
 * 
 * Responsibilities:
 * - Track workflow steps (welcome → industry → size → processes → needs → results)
 * - Manage form data state (industry, companySize, businessProcesses, specificNeeds)
 * - Detect form changes to trigger UPDATE button visibility
 * - Validate form completeness for Next button enabling
 * - Provide undo/redo capability for answer editing
 * 
 * Modularity Principle: State management is isolated from UI rendering and API calls
 */

export type WorkflowStep =
    | 'welcome'
    | 'industry'
    | 'companySize'
    | 'businessProcesses'
    | 'specificNeeds'
    | 'results'
    | 'validation'
    | 'orchestration';

export interface RequirementsFormData {
    industry: string;
    companySize: string;
    businessProcesses: string[];
    specificNeeds: string[];
}

export interface ChatHistoryMessage {
    id: string;
    question: string;
    answer: string;
    timestamp: number;
}

export interface StateSnapshot {
    step: WorkflowStep;
    formData: RequirementsFormData;
    timestamp: number;
}

type StateChangeListener = (state: RequirementsStateManager) => void;

export class RequirementsStateManager {
    private _currentStep: WorkflowStep = 'welcome';
    private _formData: RequirementsFormData = {
        industry: '',
        companySize: '',
        businessProcesses: [],
        specificNeeds: [],
    };

    private _initialFormData: RequirementsFormData | null = null;
    private _hasChanges = false;
    private _chatHistory: ChatHistoryMessage[] = [];
    private listeners: Set<StateChangeListener> = new Set();
    private history: StateSnapshot[] = [];
    private readonly MAX_HISTORY = 10;

    constructor(initialData?: RequirementsFormData) {
        if (initialData) {
            this._formData = { ...initialData };
            this._initialFormData = { ...initialData };
            this._currentStep = 'results'; // If we have initial data, we're in results mode
        }
    }

    // Getters
    get currentStep(): WorkflowStep {
        return this._currentStep;
    }

    get formData(): RequirementsFormData {
        return { ...this._formData };
    }

    get hasChanges(): boolean {
        return this._hasChanges;
    }

    get isFormComplete(): boolean {
        const { industry, companySize } = this._formData;
        // Business processes and specific needs are now optional
        return !!(
            industry.trim() &&
            companySize.trim()
        );
    }

    get isIndustryComplete(): boolean {
        return this._formData.industry !== '';
    }

    get isCompanySizeComplete(): boolean {
        return this._formData.companySize !== '';
    }

    get hasBusinessProcesses(): boolean {
        return this._formData.businessProcesses.length > 0;
    }

    get chatHistory(): ChatHistoryMessage[] {
        return [...this._chatHistory];
    }

    /**
     * Add a Q&A pair to chat history
     */
    addChatMessage(question: string, answer: string): void {
        const message: ChatHistoryMessage = {
            id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            question,
            answer,
            timestamp: Date.now(),
        };
        this._chatHistory.push(message);
        this.notifyListeners();
    }

    /**
     * Clear chat history
     */
    clearChatHistory(): void {
        this._chatHistory = [];
        this.notifyListeners();
    }

    /**
     * Subscribe to state changes
     */
    subscribe(listener: StateChangeListener): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /**
     * Notify all listeners of state change
     */
    private notifyListeners(): void {
        this.listeners.forEach(listener => listener(this));
    }

    /**
     * Save current state to history
     */
    private saveToHistory(): void {
        const snapshot: StateSnapshot = {
            step: this._currentStep,
            formData: { ...this._formData },
            timestamp: Date.now(),
        };

        this.history.push(snapshot);

        // Keep history size limited
        if (this.history.length > this.MAX_HISTORY) {
            this.history.shift();
        }
    }

    /**
     * Check if form data has changed from initial
     */
    private updateHasChanges(): void {
        if (!this._initialFormData) {
            this._hasChanges = false;
            return;
        }

        // Deep comparison of form data
        const changed =
            this._formData.industry !== this._initialFormData.industry ||
            this._formData.companySize !== this._initialFormData.companySize ||
            JSON.stringify(this._formData.businessProcesses.sort()) !==
            JSON.stringify(this._initialFormData.businessProcesses.sort()) ||
            JSON.stringify(this._formData.specificNeeds.sort()) !==
            JSON.stringify(this._initialFormData.specificNeeds.sort());

        this._hasChanges = changed;
    }

    /**
     * Set industry selection
     */
    setIndustry(industry: string): void {
        this.saveToHistory();
        this._formData.industry = industry;
        this.updateHasChanges();

        // Auto-advance to next step if in initial flow
        if (this._currentStep === 'welcome' || this._currentStep === 'industry') {
            this._currentStep = 'companySize';
        }

        this.notifyListeners();
    }

    /**
     * Set company size selection
     */
    setCompanySize(size: string): void {
        this.saveToHistory();
        this._formData.companySize = size;
        this.updateHasChanges();

        // Auto-advance to next step if in initial flow
        if (this._currentStep === 'companySize') {
            this._currentStep = 'businessProcesses';
        }

        this.notifyListeners();
    }

    /**
     * Add business process
     */
    addBusinessProcess(process: string): void {
        if (this._formData.businessProcesses.includes(process)) {
            return; // Already exists
        }

        this.saveToHistory();
        this._formData.businessProcesses.push(process);
        this.updateHasChanges();
        this.notifyListeners();
    }

    /**
     * Remove business process
     */
    removeBusinessProcess(process: string): void {
        this.saveToHistory();
        this._formData.businessProcesses = this._formData.businessProcesses.filter(
            p => p !== process
        );
        this.updateHasChanges();
        this.notifyListeners();
    }

    /**
     * Add specific need
     */
    addSpecificNeed(need: string): void {
        if (this._formData.specificNeeds.includes(need)) {
            return; // Already exists
        }

        this.saveToHistory();
        this._formData.specificNeeds.push(need);
        this.updateHasChanges();
        this.notifyListeners();
    }

    /**
     * Remove specific need
     */
    removeSpecificNeed(need: string): void {
        this.saveToHistory();
        this._formData.specificNeeds = this._formData.specificNeeds.filter(
            n => n !== need
        );
        this.updateHasChanges();
        this.notifyListeners();
    }

    /**
     * Move to next step in workflow
     */
    moveToNextStep(): void {
        this.saveToHistory();

        const stepOrder: WorkflowStep[] = [
            'welcome',
            'industry',
            'companySize',
            'businessProcesses',
            'specificNeeds',
            'results',
            'validation',
            'orchestration',
        ];

        const currentIndex = stepOrder.indexOf(this._currentStep);
        if (currentIndex < stepOrder.length - 1) {
            this._currentStep = stepOrder[currentIndex + 1];
        }

        this.notifyListeners();
    }

    /**
     * Move to specific step
     */
    moveToStep(step: WorkflowStep): void {
        this.saveToHistory();
        this._currentStep = step;
        this.notifyListeners();
    }

    /**
     * Start the workflow (from welcome)
     */
    start(): void {
        this.saveToHistory();
        this._currentStep = 'industry';
        this.notifyListeners();
    }

    /**
     * Move to results view
     */
    showResults(): void {
        this.saveToHistory();
        this._currentStep = 'results';
        this._initialFormData = { ...this._formData };
        this._hasChanges = false;
        this.notifyListeners();
    }

    /**
     * Mark changes as saved (after UPDATE completes)
     */
    markChangesSaved(): void {
        this._initialFormData = { ...this._formData };
        this._hasChanges = false;
        this.notifyListeners();
    }

    /**
     * Reset form to initial state
     */
    resetForm(): void {
        this.saveToHistory();
        this._formData = {
            industry: '',
            companySize: '',
            businessProcesses: [],
            specificNeeds: [],
        };
        this._initialFormData = null;
        this._hasChanges = false;
        this._currentStep = 'welcome';
        this.notifyListeners();
    }

    /**
     * Undo last change
     */
    undo(): boolean {
        if (this.history.length === 0) {
            return false;
        }

        const snapshot = this.history.pop()!;
        this._currentStep = snapshot.step;
        this._formData = { ...snapshot.formData };
        this.updateHasChanges();
        this.notifyListeners();
        return true;
    }

    /**
     * Get state summary for debugging
     */
    getStateSummary(): Record<string, any> {
        return {
            currentStep: this._currentStep,
            formData: this._formData,
            hasChanges: this._hasChanges,
            isFormComplete: this.isFormComplete,
            historyLength: this.history.length,
        };
    }
}
